import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom';
import { getByIdPA, patchByIdPA } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaIdCard, 
  FaSearch,
  FaUser,
  FaEnvelope,
  FaShieldAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaEye,
  FaEdit,
  FaTrash,
  FaBuilding,
  FaBriefcase,
  FaComments,
  FaGraduationCap,
  FaTimes,
  FaCopy,
  FaInfoCircle
} from 'react-icons/fa';
import { Card, Row, Col, Form, Button, Badge, Table, Modal } from 'react-bootstrap'
import './Licenses.css'

// eslint-disable-next-line
export default function (props) {
  const navigate = useNavigate()
  const { users: licenses, authenticatedUser, setUsers, setAlert } = useUserContext()

  const [keywords, setKeywords] = useState('')
  const [allLicenses, setAllLicenses] = useState([])
  const [filteredLicenses, setFilteredLicenses] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showLicenseModal, setShowLicenseModal] = useState(false)
  const [selectedLicense, setSelectedLicense] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [licenseToDelete, setLicenseToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [copiedLicense, setCopiedLicense] = useState(null)

  const handleChangeKeywords = (e) => setKeywords(e.target.value)

  const handleCopyLicense = async (licenseCode) => {
    try {
      await navigator.clipboard.writeText(licenseCode)
      setCopiedLicense(licenseCode)
      setTimeout(() => setCopiedLicense(null), 2000)
    } catch (err) {
      console.error('Error copying to clipboard:', err)
    }
  }

  // Función para normalizar texto (quitar acentos)
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  window.scrollTo({
    top: 0,
    behavior: 'auto',
  })

  const fetchData = async () => {
    setIsLoading(true)
    try {
    const response = await getByIdPA({ path: 'plan', id: authenticatedUser.userId, token: authenticatedUser.value })
    if (response.error || !Array.isArray(response.planLicenses)) {
      setUsers([])
        setAllLicenses([])
        setFilteredLicenses([])
      setAlert({
        message: response.message || 'Error en la transacción. Si persiste el error, por favor contacte a soporte.',
        msgType: 'warning'
      })
    } else {
      setUsers(response.planLicenses)
        setAllLicenses(response.planLicenses)
        setFilteredLicenses(response.planLicenses)
      }
    } catch (error) {
      setUsers([])
      setAllLicenses([])
      setFilteredLicenses([])
      setAlert({
        message: 'Error al cargar las licencias. Por favor, inténtalo de nuevo.',
        msgType: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewLicense = (license) => {
    setSelectedLicense(license)
    setShowLicenseModal(true)
  }

  const handleDeleteLicense = (license) => {
    setLicenseToDelete(license)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!licenseToDelete) return

    setIsDeleting(true)
    try {
      const response = await patchByIdPA({
        path: 'memberships',
        id: licenseToDelete.membershipId,
        data: { deleted: true },
        token: authenticatedUser.value
      })

      if (response.membershipId) {
        setUsers(licenses.filter(license => license.membershipId !== licenseToDelete.membershipId))
        setAlert({
          message: 'Licencia eliminada exitosamente',
          msgType: 'success'
        })
      } else {
        setAlert({
          message: response.message || 'Error al eliminar la licencia',
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al eliminar la licencia',
        msgType: 'error'
      })
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
      setLicenseToDelete(null)
    }
  }

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return <FaShieldAlt />
      case 'anfitrion_org':
        return <FaBuilding />
      case 'anfitrion_per':
        return <FaBriefcase />
      case 'invitado':
        return <FaUser />
      default:
        return <FaIdCard />
    }
  }

  const getStatusSwitch = (used, membershipId) => {
    return (
      <div className="status-switch">
        <input
          type="checkbox"
          id={`status-${membershipId}`}
          checked={used}
          readOnly
          className="switch-input"
        />
        <label htmlFor={`status-${membershipId}`} className="switch-label">
          <span className="switch-slider"></span>
        </label>
      </div>
    )
  }

  const getRoleBadge = (role) => {
    return (
      <span className="role-badge">
        {role || 'Usuario'}
      </span>
    )
  }

  // Efecto para filtrar licencias cuando cambian las keywords
  useEffect(() => {
    if (keywords.trim() === '') {
      setFilteredLicenses(allLicenses)
    } else {
      const normalizedKeywords = normalizeText(keywords)
      const filtered = allLicenses.filter(license =>
        normalizeText(license.licenseCode || '').includes(normalizedKeywords) ||
        normalizeText(license.userEmail || '').includes(normalizedKeywords) ||
        normalizeText(license.fullName || '').includes(normalizedKeywords) ||
        normalizeText(license.userRol || '').includes(normalizedKeywords)
      )
      setFilteredLicenses(filtered)
    }
  }, [keywords, allLicenses])

  useEffect(() => {
    fetchData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="licenses-container">
      <div className="container">
        {/* Header Section */}
        <div className="licenses-header">
          <div className="header-content">
            <div className="header-icon">
              <div className="icon-circle">
                <FaIdCard className="icon" />
          </div>
            </div>
            <div className="header-text">
              <h1 className="header-title">Licencias</h1>
              <p className="header-subtitle">Administrá y gestioná las licencias de tu plan</p>
            </div>
          </div>
            </div>

        {/* Actions and Search Section */}
        <Card className="actions-card">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={12} md={12} sm={12}>
                <div className="search-input-container">
                  <FaSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Buscar licencias por código, anfitrion, usuario o rol..."
                value={keywords}
                onChange={handleChangeKeywords}
                    className="search-input"
              />
            </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Information Legend */}
        <Card className="info-legend-card">
          <Card.Body>
            <div className="info-legend-content">
              <FaInfoCircle className="info-icon" />
              <div className="info-text">
                <h6 className="info-title">Cómo usar tus licencias</h6>
                <p className="info-description">
                  Copia y comparte el código con el nuevo anfitrión para que se registre.
                  Si una licencia ya está en uso, no se puede volver a compartir.
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Roles Information */}
        <Card className="info-legend-card">
          <Card.Body>
            <div className="info-legend-content">
              <FaInfoCircle className="info-icon" />
              <div className="info-text">
                <h6 className="info-title">Información sobre Roles</h6>
                <div className="info-description">
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Administrador:</strong>{' '}
                    Tú administras el plan. Puedes comprar licencias, asignarlas a tus anfitriones y crear sesiones dentro del sistema.
                    Tu función es gestionar el uso de las licencias y mantener activo el acceso de tu equipo.
                  </div>
                  <div>
                    <strong>Anfitrión:</strong>{' '}
                    Puedes crear y gestionar tus propias sesiones dentro del sistema.
                    Recibes una licencia asignada por tu administrador y no puedes compartirla con otros usuarios.
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Table Section */}
        <Card className="table-card">
          <Card.Body className="p-0">
            {isLoading ? (
              <div className="loading-state">
                <FaIdCard className="loading-icon" />
                <p className="mt-3">Cargando licencias...</p>
              </div>
            ) : filteredLicenses.length === 0 ? (
              <div className="empty-state">
                <FaIdCard className="empty-icon" />
                <h4>No se encontraron licencias</h4>
                <p>Intenta con otros términos de búsqueda o agrega una nueva licencia.</p>
            </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="table-responsive d-none d-lg-block">
                  <Table className="licenses-table">
            <thead>
              <tr>
                        <th>Id</th>
                        <th>Licencia</th>
                        <th>Asignado a</th>
                        <th>País</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Registro</th>
                        <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
                      {filteredLicenses
                        .sort((a, b) => b.membershipId - a.membershipId)
                        .map((license) => (
                          <tr key={license.membershipId}>
                            <td>
                              <span className="license-id">#{license.membershipId}</span>
                            </td>
                            <td>
                              {license.licenseCode ? (
                                <div className="license-code-container">
                                  {license.used ? (
                                    <span className="license-code-used">
                                      {license.licenseCode}
                                    </span>
                                  ) : (
                                    <>
                                      <span 
                                        className="license-code-copyable"
                                        onClick={() => handleCopyLicense(license.licenseCode)}
                                        title="Hacer clic para copiar"
                                      >
                                        {license.licenseCode}
                                      </span>
                                      <FaCopy className="copy-icon" />
                                      {copiedLicense === license.licenseCode && (
                                        <span className="copied-indicator">¡Copiado!</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                  <td>{license.fullName || '-'}</td>
                            <td>Costa Rica</td>
                            <td>{getRoleBadge(license.userRol)}</td>
                            <td>{getStatusSwitch(license.used, license.membershipId)}</td>
                            <td>{new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                            <td>
                              <button
                                onClick={() => handleDeleteLicense(license)}
                                className="delete-btn"
                                title="Eliminar licencia"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                    </div>

                {/* Tablet Table View */}
                <div className="table-responsive d-none d-md-block d-lg-none">
                  <Table className="licenses-table">
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Licencia</th>
                        <th>Asignado a</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLicenses
                        .sort((a, b) => b.membershipId - a.membershipId)
                        .map((license) => (
                          <tr key={license.membershipId}>
                            <td>
                              <span className="license-id">#{license.membershipId}</span>
                            </td>
                            <td>
                              {license.licenseCode ? (
                                <div className="license-code-container">
                                  {license.used ? (
                                    <span className="license-code-used">
                                      {license.licenseCode}
                                    </span>
                                  ) : (
                                    <>
                                      <span 
                                        className="license-code-copyable"
                                        onClick={() => handleCopyLicense(license.licenseCode)}
                                        title="Hacer clic para copiar"
                                      >
                                        {license.licenseCode}
                                      </span>
                                      <FaCopy className="copy-icon" />
                                      {copiedLicense === license.licenseCode && (
                                        <span className="copied-indicator">¡Copiado!</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{license.fullName || '-'}</td>
                            <td>{getRoleBadge(license.userRol)}</td>
                            <td>{getStatusSwitch(license.used, license.membershipId)}</td>
                            <td>
                    <button
                                onClick={() => handleDeleteLicense(license)}
                                className="delete-btn"
                                title="Eliminar licencia"
                              >
                                <FaTrash />
                    </button>
                            </td>
                </tr>
              ))}
            </tbody>
                  </Table>
                </div>

                {/* Mobile Card View */}
                <div className="d-block d-md-none">
                  {filteredLicenses
                    .sort((a, b) => b.membershipId - a.membershipId)
                    .map((license) => (
                      <Card key={license.membershipId} className="license-mobile-card">
                        <Card.Body>
                          <div className="license-mobile-header">
                            <div className="license-mobile-title">
                              <h6 className="mb-1">
                                {license.licenseCode ? (
                                  <div className="license-code-container">
                                    {license.used ? (
                                      <span className="license-code-used">
                                        {license.licenseCode}
                                      </span>
                                    ) : (
                                      <>
                                        <span 
                                          className="license-code-copyable"
                                          onClick={() => handleCopyLicense(license.licenseCode)}
                                          title="Hacer clic para copiar"
                                        >
                                          {license.licenseCode}
                                        </span>
                                        <FaCopy className="copy-icon" />
                                        {copiedLicense === license.licenseCode && (
                                          <span className="copied-indicator">¡Copiado!</span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </h6>
                              <small className="text-muted">ID: #{license.membershipId}</small>
                            </div>
                            <div className="license-mobile-status">
                              {getStatusSwitch(license.used, license.membershipId)}
                            </div>
                          </div>
                          
                          <div className="license-mobile-details">
                            <div className="detail-row">
                              <FaUser className="detail-icon" />
                              <span>{license.fullName || 'Sin asignar'}</span>
                            </div>
                            <div className="detail-row">
                              <span>Rol:</span>
                              {getRoleBadge(license.userRol)}
                            </div>
                          </div>

                          <div className="license-mobile-actions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewLicense(license)}
                              className="action-btn"
                            >
                              <FaEye className="me-1" />
                              Ver
                            </Button>
                            {license.used && license.userId !== license.ownerId && (
                              <Button
                                variant="outline-info"
                                size="sm"
                                as={Link}
                                to={`/licencias/${license.membershipId}`}
                                className="action-btn"
                              >
                                <FaEdit className="me-1" />
                                Editar
                              </Button>
                            )}
                            <button
                              onClick={() => handleDeleteLicense(license)}
                              className="delete-btn"
                              title="Eliminar licencia"
                            >
                              <FaTrash className="me-1" />
                              Eliminar
                            </button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* License Details Modal */}
        <Modal show={showLicenseModal} onHide={() => setShowLicenseModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <FaIdCard className="me-2" />
              Detalles de la Licencia
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedLicense && (
              <Row>
                <Col md={6}>
                  <div className="detail-item">
                    <label>Código de Licencia:</label>
                    <p>{selectedLicense.licenseCode}</p>
                  </div>
                  <div className="detail-item">
                    <label>Estado:</label>
                    <div>{getStatusSwitch(selectedLicense.used, selectedLicense.membershipId)}</div>
                  </div>
                  <div className="detail-item">
                    <label>Usuario:</label>
                    <p>{selectedLicense.fullName || 'Sin asignar'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="detail-item">
                    <label>Anfitrion:</label>
                    <p>{selectedLicense.fullName || 'Sin asignar'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Tipo de Rol:</label>
                    <div>{getRoleBadge(selectedLicense.userRol)}</div>
                  </div>
                  <div className="detail-item">
                    <label>ID de Membresía:</label>
                    <p>{selectedLicense.membershipId}</p>
                  </div>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLicenseModal(false)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal 
          show={showDeleteModal} 
          onHide={() => setShowDeleteModal(false)}
          centered
          backdrop="static"
          keyboard={false}
          className="delete-confirm-modal"
        >
          <Modal.Header className="delete-modal-header">
            <div className="modal-icon-container">
              <FaExclamationTriangle className="modal-warning-icon" />
            </div>
            <div className="modal-title-container">
              <Modal.Title className="modal-title">Confirmar Eliminación</Modal.Title>
              <p className="modal-subtitle">Esta acción no se puede deshacer</p>
            </div>
            <Button 
              variant="link" 
              onClick={() => setShowDeleteModal(false)}
              className="modal-close-btn"
              disabled={isDeleting}
            >
              <FaTimes />
            </Button>
          </Modal.Header>
          
          <Modal.Body className="delete-modal-body">
            <div className="license-preview">
              <h6 className="preview-label">Licencia a eliminar:</h6>
              <div className="license-content">
                <p className="license-text">
                  <strong>ID:</strong> #{licenseToDelete?.membershipId}
                </p>
                <p className="license-text">
                  <strong>Código:</strong> {licenseToDelete?.licenseCode}
                </p>
                <p className="license-text">
                  <strong>Anfitrion:</strong> {licenseToDelete?.fullName || 'Sin asignar'}
                </p>
              </div>
            </div>
            
            <div className="warning-message">
              <FaExclamationTriangle className="warning-icon" />
              <p>¿Estás seguro de que quieres eliminar esta licencia? Esta acción es permanente y no se puede deshacer.</p>
            </div>
          </Modal.Body>
          
          <Modal.Footer className="delete-modal-footer">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowDeleteModal(false)}
              className="cancel-delete-btn"
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDelete}
              className="confirm-delete-btn"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Eliminando...
                </>
              ) : (
                'Eliminar Licencia'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  )
}
